/**
 * MobileRecipeDetail — Recipe detail screen shown at /m/recipes/:slug.
 *
 * Layout matches the mobile mockup exactly:
 *   • Green sticky header with back arrow + "Recipe Details"
 *   • Hero image gallery (horizontal snap + dot indicator)
 *   • Title + Category icon (mortar) · Cuisine icon (chef hat)
 *   • Recipe Videos (horizontal snap-carousel of YouTube-style
 *     thumbnails with a big play overlay — thumbnails are pulled from
 *     the YouTube URL when possible so real videos render nicely)
 *   • Tags (comma-separated)
 *   • Recipe Weblinks (bulleted blue links opening in a new tab)
 *   • Free-text body — pulls from short_description + story +
 *     ingredients/steps + additional_notes, joined with line breaks
 *     so the recipe reads like an editorial write-up (matches the
 *     Thai Basil Chicken mockup which displays a long-form paragraph
 *     view rather than a checklist)
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ChefHat, Utensils, Play, Tag, Link as LinkIcon, Video } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const FP_GREEN = "#A9D64C";
const FP_GREEN_MID = "#B5DA5E";

/* Try to derive a YouTube thumbnail URL from a full YouTube link
 * so the "Recipe Videos" carousel shows real preview images, matching
 * the mobile mockup. Falls back to a grey placeholder for non-YouTube
 * links so the layout stays consistent.
 */
function youtubeThumbnail(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/i);
  return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
}

export default function MobileRecipeDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/recipes/${slug}`)
      .then((r) => setRecipe(r.data.recipe || r.data))
      .catch(() => setRecipe(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-sm opacity-60">Loading recipe…</div>
      </div>
    );
  }
  if (!recipe) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-8 text-center">
        <div>
          <div className="text-lg font-bold">Recipe not found</div>
          <Link to="/m/recipes" className="text-sm underline mt-2 inline-block" style={{ color: "#653091" }}>
            Back to Recipes
          </Link>
        </div>
      </div>
    );
  }

  // Build the image gallery: cover_photo first, then gallery[].data
  const images = [
    recipe.cover_photo,
    ...((recipe.gallery || []).map((g) => g.data).filter(Boolean)),
  ].filter(Boolean);

  const videos = [
    ...(recipe.video_urls || []),
    ...(recipe.video_url ? [recipe.video_url] : []),
  ];

  const weblinks = [
    ...(recipe.website_urls || []),
    ...(recipe.website_url ? [recipe.website_url] : []),
  ];

  const tags = recipe.tags || [];

  // The mobile detail view renders a long-form editorial body composed
  // from short_description + story + steps.text + additional_notes so
  // the recipe reads like a paragraph rather than a checklist. When a
  // recipe was submitted through link_only mode, only short_description
  // + additional_notes are present, and that's fine.
  const bodyParts = [
    recipe.short_description,
    recipe.story,
    ...(recipe.steps || []).map((s) => s.text),
    recipe.additional_notes || recipe.chef_notes,
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-white pb-16" data-testid="mobile-recipe-detail">
      {/* Header */}
      <header
        className="sticky top-0 z-30 flex items-center gap-3 px-4 py-3"
        style={{ background: `linear-gradient(180deg, ${FP_GREEN} 0%, ${FP_GREEN_MID} 100%)` }}
      >
        <button
          onClick={() => nav(-1)}
          data-testid="recipe-detail-back"
          aria-label="Back"
          className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/20"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold">Recipe Details</h1>
      </header>

      {/* Hero gallery */}
      {images.length > 0 && (
        <div className="relative" data-testid="recipe-hero-gallery">
          <div
            className="flex overflow-x-auto snap-x snap-mandatory"
            onScroll={(e) => {
              const w = e.currentTarget.clientWidth || 1;
              const idx = Math.round(e.currentTarget.scrollLeft / w);
              if (idx !== heroIdx) setHeroIdx(idx);
            }}
          >
            {images.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`${recipe.title} — ${i + 1}`}
                loading={i === 0 ? "eager" : "lazy"}
                className="w-full flex-shrink-0 snap-center object-cover"
                style={{ height: 320 }}
              />
            ))}
          </div>
          {images.length > 1 && (
            <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
              {images.map((_, i) => (
                <span
                  key={i}
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: i === heroIdx ? "#fff" : "rgba(255,255,255,0.5)" }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Title + category / cuisine */}
      <div className="px-5 pt-4">
        <h2 className="text-3xl font-bold leading-tight" data-testid="recipe-title">{recipe.title}</h2>
        <div className="mt-3 flex items-center gap-2 text-base">
          <Utensils className="w-5 h-5 opacity-70" />
          <span data-testid="recipe-category">{recipe.category || "Recipe"}</span>
        </div>
        {recipe.cuisine && (
          <div className="mt-1 flex items-center gap-2 text-base">
            <ChefHat className="w-5 h-5 opacity-70" />
            <span data-testid="recipe-cuisine">{recipe.cuisine}</span>
          </div>
        )}
      </div>

      {/* Recipe Videos */}
      {videos.length > 0 && (
        <section className="mt-5 border-t border-gray-200 pt-4" data-testid="recipe-videos">
          <div className="px-5 flex items-center gap-2 mb-3">
            <Video className="w-6 h-6" />
            <h3 className="text-xl font-bold">Recipe Videos</h3>
          </div>
          <div className="pl-5 pr-2 flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory">
            {videos.map((url, i) => {
              const thumb = youtubeThumbnail(url);
              return (
                <a
                  key={i}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`recipe-video-${i}`}
                  className="relative flex-shrink-0 w-40 h-40 rounded-2xl overflow-hidden snap-start bg-gray-200"
                >
                  {thumb ? (
                    <img src={thumb} alt={`Video ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
                  ) : null}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                      <Play className="w-6 h-6 text-black" fill="currentColor" />
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </section>
      )}

      {/* Tags */}
      {tags.length > 0 && (
        <section className="mt-5 border-t border-gray-200 pt-4" data-testid="recipe-tags">
          <div className="px-5 flex items-center gap-2 mb-2">
            <Tag className="w-6 h-6" />
            <h3 className="text-xl font-bold">Tags</h3>
          </div>
          <p className="px-5 text-base leading-snug">
            {tags.map((t) => (t.startsWith("#") ? t : `#${t}`)).join(", ")}
          </p>
        </section>
      )}

      {/* Weblinks */}
      {weblinks.length > 0 && (
        <section className="mt-5 border-t border-gray-200 pt-4" data-testid="recipe-weblinks">
          <div className="px-5 flex items-center gap-2 mb-2">
            <LinkIcon className="w-6 h-6" />
            <h3 className="text-xl font-bold">Recipe Weblinks</h3>
          </div>
          <ul className="pl-8 pr-5 space-y-2 list-disc">
            {weblinks.map((url, i) => (
              <li key={i} className="text-sm break-all">
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 underline"
                  data-testid={`recipe-weblink-${i}`}
                >
                  {url.length > 60 ? url.slice(0, 57) + "…" : url}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Free-text body */}
      {bodyParts.length > 0 && (
        <section className="mt-5 border-t border-gray-200 pt-4 px-5 pb-8 space-y-3" data-testid="recipe-body">
          {bodyParts.map((chunk, i) => (
            <p key={i} className="text-base leading-relaxed whitespace-pre-line">
              {chunk}
            </p>
          ))}
        </section>
      )}

      {/* Ingredients (if user filled them) */}
      {(recipe.ingredients || []).length > 0 && (
        <section className="border-t border-gray-200 pt-4 px-5 pb-8" data-testid="recipe-ingredients">
          <h3 className="text-xl font-bold mb-3">Ingredients</h3>
          <ul className="space-y-2 list-disc pl-5">
            {recipe.ingredients.map((ing, i) => (
              <li key={i} className="text-base">
                <b>{ing.name}</b>{ing.quantity ? ` — ${ing.quantity}${ing.unit ? " " + ing.unit : ""}` : ""}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
